import type { ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  component: TermsOfServicePage,
});

const CONTACT = "nils.zaenker@ivelion.com";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function ContactLink() {
  return (
    <a
      href={`mailto:${CONTACT}`}
      className="text-purple-600 hover:text-purple-700"
    >
      {CONTACT}
    </a>
  );
}

function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Nutzungsbedingungen
        </h1>
        <p className="text-gray-500 mb-8">Version: 02.11.2026</p>

        <div className="prose prose-gray max-w-none">
          <Section title="§ 1 Anbieter und Geltungsbereich">
            <p className="mb-4">
              Anbieter dieser Dienste ist die Ivelion Health GmbH,
              Altensteinstraße 40, 14195 Berlin, Deutschland (nachfolgend
              „Anbieter"). Diese Bedingungen regeln die Nutzung der webbasierten
              Ivelion AI Plattform einschließlich des MS-Chatbots (nachfolgend
              „Software").
            </p>
            <p className="mb-4">
              Abweichende Geschäftsbedingungen gelten nicht, es sei denn, der
              Anbieter stimmt schriftlich zu.
            </p>
          </Section>

          <Section title="§ 2 Vertragsgegenstand">
            <p className="mb-4">
              Der Anbieter stellt die jeweils aktuelle Version der Software über
              das Internet zur Nutzung bereit. Der Zugriff erfolgt über einen
              gängigen Browser auf Desktop und Mobile. Eine physische
              Überlassung findet nicht statt.
            </p>
            <p className="mb-4">
              Die Software umfasst u. a. einen Chatbot für Informationen zu
              Multipler Sklerose und die Generierung von E-Mail-Entwürfen. Die
              Nutzung ist ohne Anmeldung (anonym) oder mit Konto möglich. Mit
              einem Konto werden Ihre Chats gespeichert und sind nach der
              Anmeldung auf anderen Geräten abrufbar.
            </p>
          </Section>

          <Section title="§ 3 Medizinischer Hinweis">
            <p className="mb-4">
              Die Inhalte der Software stellen{" "}
              <strong>keinen medizinischen Rat</strong> dar. Sie ersetzen keine
              ärztliche Beratung, Diagnose oder Behandlung. Treffen Sie keine
              Gesundheitsentscheidungen allein auf Basis der Ausgaben der
              Software. In Notfällen rufen Sie den Notruf 112 oder suchen Sie
              umgehend ärztliche Hilfe.
            </p>
          </Section>

          <Section title="§ 4 Leistungen und Verfügbarkeit">
            <p className="mb-4">
              (1) Der Anbieter bemüht sich um eine monatliche Verfügbarkeit von
              mindestens 95 %. Zeiten geplanter Wartung und unvorhersehbare
              Ereignisse (z. B. höhere Gewalt, Drittnetzstörungen) sind hiervon
              ausgenommen.
            </p>
            <p className="mb-4">
              (2) Funktionsumfang und Inhalte können weiterentwickelt, geändert
              oder eingestellt werden. Wesentliche Änderungen werden angemessen
              angekündigt.
            </p>
            <p className="mb-4">
              (3) Supportanfragen können per E-Mail an <ContactLink />{" "}
              gestellt werden. Die Bearbeitung erfolgt nach Priorität und
              Eingang.
            </p>
          </Section>

          <Section title="§ 5 Zulässige Nutzung">
            <p className="mb-4">
              Sie nutzen die Software nur im Rahmen geltenden Rechts. Untersagt
              sind insbesondere: rechtswidrige Inhalte, Belästigung, Spam, die
              Umgehung von Sicherheitsmechanismen, Reverse Engineering sowie
              automatisiertes Massenscraping ohne schriftliche Zustimmung.
            </p>
            <p className="mb-4">
              Sie speichern keine Inhalte, deren Rechte Ihnen nicht zustehen
              oder die Rechte Dritter verletzen.
            </p>
          </Section>

          <Section title="§ 6 Registrierung und Konto">
            <p className="mb-4">
              (1) Für die Nutzung ohne Konto ist keine Registrierung
              erforderlich. Ein Konto legen Sie mit E-Mail-Adresse und Passwort
              an. Es ist erst nutzbar, nachdem Sie Ihre E-Mail-Adresse über den
              Link in unserer Bestätigungs-E-Mail bestätigt haben.
            </p>
            <p className="mb-4">
              (2) Jedes Konto gehört einer Person. Geben Sie Ihre Zugangsdaten
              nicht weiter. Sie sind für Handlungen über Ihr Konto
              verantwortlich. Wenn Sie vermuten, dass jemand anderes Ihr Konto
              nutzt, ändern Sie Ihr Passwort und schreiben Sie uns an{" "}
              <ContactLink />.
            </p>
            <p className="mb-4">
              (3) Wir senden Ihnen nur E-Mails, die Ihr Konto betreffen.
            </p>
          </Section>

          <Section title="§ 7 Rechte an Inhalten; KI-Ausgaben">
            <p className="mb-4">
              Eingaben und hochgeladene Inhalte („Nutzerinhalte") verbleiben bei
              Ihnen. Sie räumen dem Anbieter eine einfache, widerrufliche,
              weltweite Lizenz zur Verarbeitung der Nutzerinhalte zum Betrieb,
              zur Sicherheit und zur Verbesserung der Software ein.
            </p>
            <p className="mb-4">
              KI-Ausgaben können fehlerhaft, unvollständig oder veraltet sein.
              Prüfen Sie die Ergebnisse vor eigener Nutzung, insbesondere vor
              Außenkommunikation oder Gesundheitsentscheidungen.
            </p>
          </Section>

          <Section title="§ 8 Vergütung">
            <p className="mb-4">
              Der Dienst ist derzeit kostenfrei. Der Anbieter kann künftig
              entgeltliche Pläne einführen. Darüber wird vorab informiert; für
              bestehende Verträge gelten Änderungen erst nach Zustimmung des
              Nutzers.
            </p>
          </Section>

          <Section title="§ 9 Gewährleistung und Haftung">
            <p className="mb-4">
              (1) Der Anbieter leistet keine Gewähr für die Eignung der Software
              für einen bestimmten Zweck.
            </p>
            <p className="mb-4">
              (2) Der Anbieter haftet unbegrenzt für Vorsatz und grobe
              Fahrlässigkeit, für Schäden aus der Verletzung von Leben, Körper
              oder Gesundheit sowie nach dem Produkthaftungsgesetz.
            </p>
            <p className="mb-4">
              (3) Bei einfacher Fahrlässigkeit haftet der Anbieter nur bei
              Verletzung wesentlicher Vertragspflichten (Kardinalpflichten) und
              beschränkt auf den vorhersehbaren, typischen Schaden. Eine Haftung
              für entgangenen Gewinn ist ausgeschlossen, es sei denn, es greift
              Abs. 2.
            </p>
          </Section>

          <Section title="§ 10 Datenschutz und Vertraulichkeit">
            <p className="mb-4">
              Für die Verarbeitung personenbezogener Daten gelten die{" "}
              <Link
                to="/privacy"
                className="text-purple-600 hover:text-purple-700"
              >
                Datenschutzhinweise
              </Link>
              . Vertrauliche Informationen werden nur insoweit verarbeitet, wie
              es für den Betrieb der Software erforderlich ist.
            </p>
            <p className="mb-4">
              Zur Verbesserung unserer Dienste und zur Analyse der geografischen
              Verteilung unserer Nutzer erfassen wir zu Beginn jeder
              Chat-Sitzung Ihre ungefähre Position basierend auf Ihrer
              IP-Adresse (Stadt, Bundesland und Koordinaten). Dies hilft uns,
              Sie mit nahegelegenen Studienzentren in Verbindung zu bringen. Die
              IP-Adresse selbst wird nicht gespeichert.
            </p>
          </Section>

          <Section title="§ 11 Laufzeit und Kündigung">
            <p className="mb-4">
              Da die Nutzung der Anwendung ohne Registrierung möglich ist und
              keine Vertragsbindung besteht, ist keine Kündigung erforderlich.
              Sie können die Nutzung jederzeit beenden. Ihre Daten werden für
              die Dauer der Nutzung und darüber hinaus zur Serviceverbesserung
              und zu Forschungszwecken gespeichert. Sie können jederzeit die
              Löschung Ihrer Daten gemäß Art. 17 DSGVO per E-Mail an{" "}
              <ContactLink /> verlangen.
            </p>
            <p className="mb-4">
              Mit einem Konto besteht ein Nutzungsverhältnis auf unbestimmte
              Zeit. Sie können es jederzeit beenden, indem Sie Ihr Konto auf der
              Kontoseite löschen. Dabei werden Ihre Chats, Ihre Anmeldung und
              Ihre Kontodaten sofort gelöscht.
            </p>
            <p className="mb-4">
              Der Anbieter kann Konten sperren oder löschen, wenn ein Verstoß
              gegen diese Bedingungen oder ein Missbrauch vorliegt.
            </p>
          </Section>

          <Section title="§ 12 Minderjährige">
            <p className="mb-4">
              Der Dienst richtet sich an Personen ab 16 Jahren. Jüngere Personen
              benötigen die Zustimmung der Erziehungsberechtigten.
            </p>
          </Section>

          <Section title="§ 13 Änderungen der Bedingungen">
            <p className="mb-4">
              Der Anbieter kann diese Bedingungen anpassen. Über wesentliche
              Änderungen wird in der Software informiert. Widersprechen Sie,
              müssen Sie die Nutzung einstellen; setzen Sie die Nutzung fort,
              gilt dies als Zustimmung zur geänderten Fassung.
            </p>
          </Section>

          <Section title="§ 14 Anwendbares Recht und Gerichtsstand">
            <p className="mb-4">
              Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Ist
              der Nutzer Kaufmann, ist ausschließlicher Gerichtsstand der Sitz
              des Anbieters: Berlin.
            </p>
          </Section>

          <Section title="§ 15 Kontakt">
            <p className="mb-4">
              Ivelion Health GmbH <br /> E-Mail: <ContactLink /> <br />{" "}
              Anschrift: Altensteinstraße 40, 14195 Berlin, Deutschland ·
              <br /> Register: HRB 281646 B <br /> Vertretung: Nils Zaenker.
            </p>
          </Section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
          <Link
            to="/"
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            ← Zurück zum Chat
          </Link>
          <Link
            to="/privacy"
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Datenschutzhinweise →
          </Link>
        </div>
      </div>
    </div>
  );
}
